import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css, keyframes, RuleSet } from "styled-components";
import { Tooltip } from "antd";
import { RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  AI_GRADIENT,
  compassLightGreen,
  darkGreen,
  greyText,
  lightGreyOutline,
} from "../../styles";
import { Avatar } from "../../components/visuals/Avatar";
import aiLogo from "../../assets/svg/ai_logo.svg";
import { AssessmentTriggerModalV2 } from "../../components/common/AIModals";
import { useGetEngagementAssessmentV2 } from "../../hooks/useGetEngagementAssessmentV2";
import { buildViewModel } from "./AssessmentView/v2/assessmentViewModel";
import { FlagStack } from "./AssessmentView/v2/assessmentAtoms";
import { StateSegments } from "./AssessmentView/v2/AssessmentRunBar";

dayjs.extend(relativeTime);

const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";

const flow = keyframes`
  0% { background-position: 0% 50%, 0% 50%; }
  100% { background-position: 0% 50%, 300% 50%; }
`;

const breathe = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(43, 179, 243, 0), 0 6px 18px rgba(21, 50, 48, 0.06); }
  50% { box-shadow: 0 0 0 4px rgba(43, 179, 243, 0.08), 0 8px 24px rgba(58, 92, 245, 0.16); }
`;

const shimmer = keyframes`
  from { background-position: 200% 50%; }
  to { background-position: -200% 50%; }
`;

type PillTone = "summary" | "cta" | "running";

const gradientBorder = (flowMs: number): RuleSet<object> => css`
  border: 1.5px solid transparent;
  background:
    linear-gradient(white, white) padding-box,
    linear-gradient(
        90deg,
        #6ef2a6,
        #32e0c4,
        #2bb3f3,
        #3a5cf5,
        #2bb3f3,
        #32e0c4,
        #6ef2a6
      )
      border-box;
  background-size:
    100% 100%,
    300% 100%;
  animation:
    ${flow} ${flowMs}ms linear infinite,
    ${breathe} 2.8s ease-in-out infinite;
`;

const TONE: Record<PillTone, RuleSet<object>> = {
  summary: css`
    background: white;
    box-shadow: inset 0 0 0 1px ${lightGreyOutline};

    &:hover {
      transform: translateY(-1px);
      box-shadow:
        inset 0 0 0 1px ${lightGreyOutline},
        0 6px 18px rgba(21, 50, 48, 0.07);
    }
  `,
  cta: css`
    ${gradientBorder(3500)}

    &:hover {
      transform: translateY(-1px);
    }
  `,
  running: css`
    ${gradientBorder(1400)}
    cursor: progress;
  `,
};

const Pill = styled.button<{ $tone: PillTone }>`
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 0 14px 0 8px;
  border-radius: 999px;
  cursor: pointer;
  font-family: "Apercu";
  font-size: 12.5px;
  color: ${darkGreen};
  white-space: nowrap;
  transition:
    transform 0.2s ${EASE},
    box-shadow 0.2s ease;

  &:disabled {
    cursor: default;
  }

  ${({ $tone }): RuleSet<object> => TONE[$tone]}

  &:focus-visible {
    outline: 2px solid ${compassLightGreen};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Label = styled.span`
  font-family: "ApercuMedium";
  font-size: 13px;
`;

const CtaLabel = styled(Label)`
  background: ${AI_GRADIENT};
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
`;

const RunningLabel = styled(Label)`
  background: linear-gradient(
    90deg,
    ${greyText} 0%,
    ${greyText} 35%,
    #2bb3f3 50%,
    ${greyText} 65%,
    ${greyText} 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: ${shimmer} 1.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 14px;
  background: ${lightGreyOutline};
  flex-shrink: 0;
`;

const Item = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Muted = styled.span`
  color: ${greyText};
`;

const Arrow = styled.span`
  display: inline-flex;
  font-size: 9px;
  color: ${greyText};
  transition: transform 0.2s ${EASE};

  ${Pill}:hover & {
    transform: translateX(2px);
  }
`;

export const AssessmentSummaryButton = ({
  engagementId,
  onOpen,
}: AssessmentSummaryButtonProps): JSX.Element => {
  const { runs, isLoading, fetchHistory } = useGetEngagementAssessmentV2();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);

  useEffect(() => {
    if (engagementId) fetchHistory(engagementId);
  }, [engagementId, fetchHistory]);

  const latest = runs.length > 0 ? runs[0] : null;
  const view = useMemo(() => (latest ? buildViewModel(latest) : null), [latest]);

  const handleComplete = useCallback((): void => {
    if (engagementId) fetchHistory(engagementId);
  }, [engagementId, fetchHistory]);

  const avatar = <Avatar username="Compass AI" image={aiLogo} size="small" />;

  const modal = engagementId ? (
    <AssessmentTriggerModalV2
      show={showModal}
      onClose={(): void => setShowModal(false)}
      mode="single"
      engagementRecordId={engagementId}
      onSingleComplete={handleComplete}
      onRunningChange={setIsRunning}
    />
  ) : null;

  if (isRunning) {
    return (
      <>
        <Pill
          type="button"
          $tone="running"
          disabled
          aria-busy="true"
          aria-label="AI assessment running"
        >
          {avatar}
          <RunningLabel>Assessing engagement…</RunningLabel>
        </Pill>
        {modal}
      </>
    );
  }

  if (!view || !latest) {
    const pending = isLoading || !engagementId;
    return (
      <>
        <Pill
          type="button"
          $tone={pending ? "summary" : "cta"}
          disabled={pending}
          onClick={(): void => setShowModal(true)}
          aria-label="Run AI assessment"
        >
          {avatar}
          {pending ? (
            <Muted>AI Assessment</Muted>
          ) : (
            <CtaLabel>Run AI assessment</CtaLabel>
          )}
        </Pill>
        {modal}
      </>
    );
  }

  const flagged = view.sinsFlagged > 0;
  const assessedAt = dayjs(latest.assessed_at);

  return (
    <>
    <Tooltip
      title={`AI assessment · ${view.pillarsMet} of ${view.pillarsTotal} pillars met · ${assessedAt.fromNow()}`}
      placement="bottom"
    >
      <Pill
        type="button"
        $tone="summary"
        onClick={onOpen}
        aria-label="Open AI assessment"
      >
        {avatar}
        <Item>
          <FlagStack flags={view.sinRows.map((s) => s.isFlagged)} />
          {flagged ? `${view.sinsFlagged} flagged` : "Clean"}
        </Item>
        <Divider />
        <Item>
          <StateSegments states={view.pillarRows.map((p) => p.state)} />
          <Muted>
            {view.pillarsMet}/{view.pillarsTotal}
          </Muted>
        </Item>
        <Arrow>
          <RightOutlined />
        </Arrow>
      </Pill>
    </Tooltip>
    {modal}
    </>
  );
};

type AssessmentSummaryButtonProps = {
  engagementId: string | undefined;
  onOpen: () => void;
};
